/**
 * Adapter module — backend enum literal → operator-facing Spanish label.
 *
 * The CIRCA backend speaks ~7 closed enum sets (alert types, severities,
 * channel statuses, transcoding statuses, asset types, automation events,
 * output destinations). The v.3 operator dashboard speaks Spanish operator
 * vocabulary. This file is the SINGLE seam between the two — every UI
 * component that needs to render a backend literal must go through one of
 * the `adapt*` functions exported here (FR-005, FR-018, NFR-005).
 *
 * Built by T306 against the failing purity tests written by T305
 * (`tests/adapters/adapters.purity.test.ts`) and the guardrail in
 * `tests/guardrails/adapters.enum.test.ts`. Both source their literal lists
 * from `tests/fixtures/enums.snapshot.ts` (generated from `convex/schema.ts`
 * by T302), NEVER from the adapter itself — the contract is asymmetric on
 * purpose so a missing literal makes the test wrong AND the adapter wrong
 * in lockstep cannot happen.
 *
 * Three load-bearing invariants:
 *
 *   1. **Coverage** — every literal in the enum snapshot maps to a
 *      non-empty, paraphrased Spanish string. A label that echoes its
 *      backend token (e.g. `'failed' -> 'failed'`) is a translation defect
 *      and the labels-invariant test pins that.
 *
 *   2. **Transcoding 9 -> 4 collapse, failure-preserving** — the 9 backend
 *      transcoding states collapse to exactly 4 visible buckets
 *      ({pending, in_progress, completed, failure}). Both `failed` AND
 *      `dead` MUST land in the visible `'failure'` bucket — never folded
 *      into `'in_progress'` or hidden. The canonical operator-trust defect
 *      FR-005 names by name is exactly that hiding.
 *
 *   3. **Honest default branch** — any unmapped future literal (a 54th alert
 *      type, an 8th automation event, etc.) returns a generic Spanish
 *      fallback AND emits `logger.warn('unmapped_enum', { setName, literal })`
 *      so the gap is observable in production. A silent fallback that
 *      returns the backend token verbatim leaks engineering vocabulary; a
 *      hard throw crashes the surface. Both are rejected by the tests.
 *
 * The label maps double as the public `*_LABELS` constants consumed by the
 * guardrail test via `src/lib/labels.ts` (which re-exports from this file —
 * single source of truth, no parallel maintenance).
 */

import { logger } from './logger';
import {
  ALERT_TYPE_KEYS,
  ALERT_SEVERITY_KEYS,
  CHANNEL_STATUS_KEYS,
  TRANSCODING_STATUS_KEYS,
  ASSET_TYPE_KEYS,
  AUTOMATION_EVENT_KEYS,
  OUTPUT_DESTINATION_KEYS,
  TRANSCODING_DISPLAY_BUCKETS,
  type AlertType,
  type AlertSeverity,
  type ChannelStatus,
  type TranscodingStatus,
  type AssetType,
  type AutomationEvent,
  type OutputDestination,
  type TranscodingDisplayBucket,
} from './adapters.keys';

// ---------------------------------------------------------------------------
// Unmapped-enum telemetry (NFR-005)
// ---------------------------------------------------------------------------

/**
 * Single helper so every default branch emits the same shape. The purity test
 * asserts the message contains `'unmapped_enum'` and the context carries
 * both the set name and the literal; keeping one helper guarantees that.
 */
function warnUnmapped(setName: string, literal: string): void {
  logger.warn('unmapped_enum', { setName, literal });
}

// ---------------------------------------------------------------------------
// Generic Spanish fallback strings — never the backend token verbatim
// ---------------------------------------------------------------------------

const GENERIC_ALERT_TYPE = 'Tipo de alerta no reconocido';
const GENERIC_ALERT_SEVERITY = 'Severidad no reconocida';
const GENERIC_CHANNEL_STATUS = 'Estado de canal no reconocido';
const GENERIC_ASSET_TYPE = 'Tipo de recurso no reconocido';
const GENERIC_AUTOMATION_EVENT = 'Evento de automatización no reconocido';
const GENERIC_OUTPUT_DESTINATION = 'Destino no reconocido';

// ---------------------------------------------------------------------------
// ALERT_TYPE_LABELS — 53 entries, schema order (FR-005)
//
// Spanish operator copy. No backend token echoed. No engineering term from
// the glossary forbidden lists (no "transcodificación", "marcador SCTE",
// "nodo de borde", "drenar", "flota", etc. — see tests/fixtures/glossary.es).
// ---------------------------------------------------------------------------

export const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  drift_exceeded:                'Desviación temporal excedida',
  stream_offline:                'Transmisión sin conexión',
  zombie_ad:                     'Anuncio fantasma',
  zombie_ad_break:               'Pausa publicitaria fantasma',
  heartbeat_stale:               'Sin señal de actividad',
  transcode_failure:             'Falla al procesar el video',
  dead_letter:                   'Trabajo agotado tras reintentos',
  circuit_breaker_trip:          'Protección automática activada',
  stuck_job:                     'Trabajo atascado',
  source_validation_failure:     'Falla de validación de la fuente',
  s3_upload_failure:             'Falla al subir el archivo',
  edge_node_offline:             'Servidor de entrega sin conexión',
  youtube_stream_offline:        'Transmisión de YouTube sin conexión',
  stream_stall:                  'Transmisión detenida',
  slow_upload:                   'Subida lenta',
  high_segment_gap:              'Interrupciones de transmisión altas',
  spot_termination_notice:       'Aviso de terminación del servidor',
  baton_pass_initiated:          'Traspaso iniciado',
  baton_pass_completed:          'Traspaso completado',
  baton_pass_failed:             'Traspaso fallido',
  baton_pass_provisioning:       'Traspaso preparando recursos',
  baton_pass_partial:            'Traspaso parcial',
  baton_pass_cascading_failure:  'Traspaso con falla en cascada',
  analysis_failure:              'Falla de análisis',
  analysis_timeout:              'Análisis sin respuesta',
  bridge_relay_failed:           'Puente con falla al retransmitir',
  bridge_segment_gap:            'Puente con interrupciones',
  bridge_worker_down:            'Procesador del puente caído',
  bridge_dlq_messages:           'Puente con mensajes pendientes',
  bridge_breaker_tripped:        'Puente con protección activada',
  bridge_breaker_recovered:      'Puente recuperado',
  bridge_config_error:           'Puente con error de configuración',
  bridge_stall_detected:         'Puente detenido',
  bridge_relay_latency:          'Puente con latencia alta',
  batch_report_silence:          'Reporte por lotes sin actividad',
  slo_rollup_permanent_failure:  'Resumen SLO con falla permanente',
  slo_data_stale:                'Datos SLO desactualizados',
  ad_marker_imbalance:           'Desbalance de marcas publicitarias',
  o_and_o_health_failure:        'Falla de salud del reproductor propio',
  oo_worker_down:                'Procesador del reproductor caído',
  oo_high_fallback:              'Reproductor con respaldo elevado',
  oo_high_ttfb:                  'Reproductor con tiempo de respuesta alto',
  oo_probe_failure:              'Falla de monitoreo del reproductor',
  oo_manifest_unhealthy:         'Lista del reproductor con problemas',
  zombie_stream_detected:        'Transmisión fantasma detectada',
  oo_cue_imbalance:              'Desbalance de señales del reproductor',
  zombie_stream_type2:           'Transmisión fantasma tipo 2',
  workflow_stuck:                'Flujo de trabajo atascado',
  workflow_slow:                 'Flujo de trabajo lento',
  cron_subtask_failure:          'Tarea programada con falla',
  telemetry_pipeline_degraded:   'Telemetría degradada',
  brute_force_suspected:         'Posible ataque de fuerza bruta',
  drift_report_truncated:        'Reporte de desviación truncado',
};

// ---------------------------------------------------------------------------
// ALERT_SEVERITY_LABELS — 4 entries
// ---------------------------------------------------------------------------

export const ALERT_SEVERITY_LABELS: Record<AlertSeverity, string> = {
  info:     'Informativo',
  warning:  'Atención',
  error:    'Falla',
  critical: 'Crítico',
};

// ---------------------------------------------------------------------------
// CHANNEL_STATUS_LABELS — 6 entries (ported semantic from
// frontend-v2/src/lib/adapters.ts `toMockupStatus`, extended to Spanish so the
// `label !== literal` purity invariant holds — frontend-v2's `idle/error`
// passthroughs would fail that test).
// ---------------------------------------------------------------------------

export const CHANNEL_STATUS_LABELS: Record<ChannelStatus, string> = {
  idle:     'Inactivo',
  starting: 'Iniciando',
  running:  'En vivo',
  ad_break: 'Pausa publicitaria',
  stopping: 'Deteniendo',
  error:    'Falla',
};

// ---------------------------------------------------------------------------
// TRANSCODING_DISPLAY_STATE — 9 backend literals -> 4 visible buckets.
// Both `failed` and `dead` land in the visible 'failure' bucket
// (NEVER 'in_progress'). The four-bucket vocabulary is exactly
// {pending, in_progress, completed, failure}.
// ---------------------------------------------------------------------------

/**
 * In-progress transcoding literals that post-date the frozen FR-005 9-literal
 * snapshot. Kept OUT of `TRANSCODING_DISPLAY_STATE` (which the guardrail test
 * pins at exactly 9 keys) so the snapshot-coverage tests stay GREEN, while
 * `adaptTranscodingStatus` still resolves them to a visible in-progress bucket
 * instead of the loud-fail 'failure' default.
 */
const EXTENDED_IN_PROGRESS_STATUSES: ReadonlySet<string> = new Set<string>(['finalizing']);

export const TRANSCODING_DISPLAY_STATE: Record<TranscodingStatus, TranscodingDisplayBucket> = {
  pending:    'pending',
  validating: 'in_progress',
  queued:     'in_progress',
  dispatched: 'in_progress',
  processing: 'in_progress',
  uploading:  'in_progress',
  completed:  'completed',
  failed:     'failure',
  dead:       'failure',
};

// ---------------------------------------------------------------------------
// ASSET_TYPE_LABELS — 13 entries (includes the 3 audio types from
// circa-audio-v1d: podcast_episode, audio_companion, audio_only_clip).
// ---------------------------------------------------------------------------

export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  vod:              'Video a demanda',
  filler:           'Relleno',
  house_ad:         'Anuncio propio',
  bumper:           'Cortinilla',
  slate:            'Pantalla de espera',
  promo:            'Promoción',
  movie:            'Película',
  shortFormVideo:   'Video corto',
  tvSpecial:        'Especial de televisión',
  series:           'Serie',
  podcast_episode:  'Episodio de podcast',
  audio_companion:  'Audio acompañante',
  audio_only_clip:  'Clip de solo audio',
};

// ---------------------------------------------------------------------------
// AUTOMATION_EVENT_LABELS — 7 entries (inline union at
// automations.trigger.event in convex/schema.ts).
// ---------------------------------------------------------------------------

export const AUTOMATION_EVENT_LABELS: Record<AutomationEvent, string> = {
  video_uploaded:     'Video subido',
  video_completed:    'Video completado',
  schedule_published: 'Programación publicada',
  channel_started:    'Canal iniciado',
  channel_stopped:    'Canal detenido',
  alert_created:      'Alerta creada',
  slo_breached:       'SLO incumplido',
};

// ---------------------------------------------------------------------------
// OUTPUT_DESTINATION_LABELS — 3 entries (inline union at
// channels.outputDestination in convex/schema.ts).
//
// Semantic ported from frontend-v2/src/lib/adapters.ts `toDestinationsArray`:
// frontend-v2 returns an ARRAY of display strings (one per destination); the
// v.3 adapter returns ONE label per literal (the matching dropdown / pill UI
// renders a single string per row). The translation pairs are equivalent —
// `'both'` still means YouTube + reproductor propio.
// ---------------------------------------------------------------------------

export const OUTPUT_DESTINATION_LABELS: Record<OutputDestination, string> = {
  youtube_live: 'YouTube',
  o_and_o:      'Reproductor propio',
  both:         'Ambos destinos',
};

// ---------------------------------------------------------------------------
// Adapter functions — 7 of them, all (literal: string) => string
// ---------------------------------------------------------------------------

/**
 * Translate a backend `alertType` literal to its Spanish operator label.
 * Unknown literals fall back to a generic label AND emit unmapped-enum
 * telemetry (NFR-005).
 */
export function adaptAlertType(literal: string): string {
  const label = (ALERT_TYPE_LABELS as Record<string, string>)[literal];
  if (label !== undefined) return label;
  warnUnmapped('alertType', literal);
  return GENERIC_ALERT_TYPE;
}

/**
 * Translate a backend `alertSeverity` literal to its Spanish operator label.
 */
export function adaptAlertSeverity(literal: string): string {
  const label = (ALERT_SEVERITY_LABELS as Record<string, string>)[literal];
  if (label !== undefined) return label;
  warnUnmapped('alertSeverity', literal);
  return GENERIC_ALERT_SEVERITY;
}

/**
 * Translate a backend `channelStatus` literal to its Spanish operator label.
 * Semantic ported from frontend-v2's `toMockupStatus` (running -> "live"
 * concept, ad_break -> "pausa publicitaria" concept) with Spanish copy so
 * the `label !== literal` purity invariant holds for every literal.
 */
export function adaptChannelStatus(literal: string): string {
  const label = (CHANNEL_STATUS_LABELS as Record<string, string>)[literal];
  if (label !== undefined) return label;
  warnUnmapped('channelStatus', literal);
  return GENERIC_CHANNEL_STATUS;
}

/**
 * Collapse a backend `transcodingStatus` literal (9 values) into one of the
 * 4 visible display buckets {pending, in_progress, completed, failure}.
 *
 * Both `failed` and `dead` MUST resolve to 'failure' — hiding a dead-letter
 * job behind "in progress" is the canonical operator-trust defect FR-005
 * names by name. Unknown literals fall back to 'failure' (loud-fail, never
 * hidden) AND emit telemetry.
 */
export function adaptTranscodingStatus(literal: string): TranscodingDisplayBucket {
  const bucket = (TRANSCODING_DISPLAY_STATE as Record<string, TranscodingDisplayBucket>)[literal];
  if (bucket !== undefined) return bucket;
  // 'finalizing' was added to the backend transcodingStatus union AFTER the
  // FR-005 9-literal snapshot was frozen (container uploading the transcoded
  // HLS OUTPUT to S3 — distinct from source-upload 'uploading'). It is an
  // in-progress state, so map it explicitly rather than letting it fall through
  // to the loud-fail 'failure' default. It is intentionally NOT added to the
  // closed `TRANSCODING_DISPLAY_STATE` map (keyed by the 9-literal
  // `TranscodingStatus` type and guardrail-pinned at 9 literals) — this is the
  // seam for post-snapshot in-progress literals, no telemetry warn.
  if (EXTENDED_IN_PROGRESS_STATUSES.has(literal)) return 'in_progress';
  warnUnmapped('transcodingStatus', literal);
  // Loud-fail default — keeps a future literal visible to operators rather
  // than buried in "in progress". The four-bucket invariant holds because
  // 'failure' is itself one of the legal buckets.
  return 'failure';
}

/**
 * Translate a backend `assetType` literal to its Spanish operator label.
 * 13 entries; covers the 3 audio types added by circa-audio-v1d.
 */
export function adaptAssetType(literal: string): string {
  const label = (ASSET_TYPE_LABELS as Record<string, string>)[literal];
  if (label !== undefined) return label;
  warnUnmapped('assetType', literal);
  return GENERIC_ASSET_TYPE;
}

/**
 * Translate a backend `automationEvent` literal (inline union) to its
 * Spanish operator label.
 */
export function adaptAutomationEvent(literal: string): string {
  const label = (AUTOMATION_EVENT_LABELS as Record<string, string>)[literal];
  if (label !== undefined) return label;
  warnUnmapped('automationEvent', literal);
  return GENERIC_AUTOMATION_EVENT;
}

/**
 * Translate a backend `outputDestination` literal (inline union) to its
 * Spanish operator label.
 */
export function adaptOutputDestination(literal: string): string {
  const label = (OUTPUT_DESTINATION_LABELS as Record<string, string>)[literal];
  if (label !== undefined) return label;
  warnUnmapped('outputDestination', literal);
  return GENERIC_OUTPUT_DESTINATION;
}

// ---------------------------------------------------------------------------
// Re-export the key arrays from `./adapters.keys` so downstream callers can
// `import { ALERT_TYPE_KEYS } from '@/lib/adapters'` without two import lines.
// ---------------------------------------------------------------------------

export {
  ALERT_TYPE_KEYS,
  ALERT_SEVERITY_KEYS,
  CHANNEL_STATUS_KEYS,
  TRANSCODING_STATUS_KEYS,
  ASSET_TYPE_KEYS,
  AUTOMATION_EVENT_KEYS,
  OUTPUT_DESTINATION_KEYS,
  TRANSCODING_DISPLAY_BUCKETS,
} from './adapters.keys';

export type {
  AlertType,
  AlertSeverity,
  ChannelStatus,
  TranscodingStatus,
  AssetType,
  AutomationEvent,
  OutputDestination,
  TranscodingDisplayBucket,
};
